import { useCallback, useMemo } from 'react';
import {
	TabOption,
	TabOptionUnion,
	TabSelectItem,
	TValue,
	TabWithSelectTab,
	SelectTabValue,
} from 'src/components/primitives/Tabs/types';

/**
 * Note, due to combining tabs and a select into one, we need to discern between
 * the "active tab" and the overall "value" of the component, which is the union
 * of the tab option values (T) and select item values (S).
 */
interface Params<T extends string, S extends string> {
	/**
	 * The set of options excluding the select option.
	 */
	tabOptions: TabOption<T>[];
	/**
	 * The items available to choose from the `Select`.
	 */
	selectItems: TabSelectItem<S>[];
	selectPlaceholder: string;
	/**
	 * Overall value of the tab component (union of tab and select item values).
	 */
	value: TValue<T> | S;
	defaultSelectValue?: TabSelectItem<S>;
	/**
	 * Callback for when the overall value of the tab component is changed.
	 */
	onValueChange: (value: TValue<T> | S) => void;
	onTabChange?: (value: TabWithSelectTab<T>) => void;
	onSelectValueChange?: (value: S) => void;
}

const SELECT_TAB_VALUE: SelectTabValue = 'select';

/**
 * Facilitates setting up a `Tabs` component with a `Select` option.
 * See example useage in `Tabs.stories.tsx`.
 */
export default function useTabsWithSelect<T extends string, S extends string>({
	tabOptions,
	selectItems,
	selectPlaceholder,
	value,
	defaultSelectValue,
	onValueChange,
	onTabChange,
	onSelectValueChange,
}: Params<T, S>) {
	// If the active tab value can't be found in the tab options, then it's coming
	// from a select item, and so the select tab is the current active tab.
	const activeTab: TabWithSelectTab<T> = useMemo(() => {
		return (
			tabOptions.find((option) => option.value === value)?.value ??
			SELECT_TAB_VALUE
		);
	}, [tabOptions, value]);

	const handleTabChange = useCallback(
		(newTabValue: TabWithSelectTab<T>) => {
			onTabChange?.(newTabValue);

			if (isNotSelect(newTabValue)) {
				onValueChange?.(newTabValue);
			}
		},
		[onTabChange, onValueChange]
	);

	const handleSelectValueChange = useCallback(
		(newSelectValue: S) => {
			onSelectValueChange?.(newSelectValue);
			onValueChange?.(newSelectValue);
		},
		[onSelectValueChange, onValueChange]
	);

	const selectValue = useMemo(() => {
		if (activeTab !== SELECT_TAB_VALUE) {
			return;
		}

		return (
			selectItems.find((option) => option.value === value) ?? defaultSelectValue
		);
	}, [selectItems, value, activeTab]);

	const tabOptionsWithSelect: TabOptionUnion<TabWithSelectTab<T>, S>[] =
		useMemo(() => {
			return [
				...tabOptions,
				{
					label: selectPlaceholder,
					value: 'select',
					selectOptions: selectItems,
					selectedValue: selectValue,
					onSelectedValueChange: handleSelectValueChange,
				},
			];
		}, [
			tabOptions,
			selectPlaceholder,
			selectItems,
			selectValue,
			defaultSelectValue,
			handleSelectValueChange,
		]);

	return {
		activeTab,
		handleTabChange,
		tabOptionsWithSelect,
	};
}

function isNotSelect<T extends string>(
	value: TabWithSelectTab<T>
): value is TValue<T> {
	return value !== SELECT_TAB_VALUE;
}
